@if (!isReadonly) {
  <div class="flex items-center justify-end px-4 md:px-6 pb-3">
    <button
      type="button"
      class="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 text-sm font-medium rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      [disabled]="isCreatingRow || !!editingComponentId"
      (click)="startCreateRow()"
    >
      + {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.ADD' | translate }}
    </button>
  </div>
}

@if (isLoading) {
  <div class="px-4 md:px-6 py-3">
    <div class="spinner-border text-primary" role="status">
      <span class="visually-hidden">{{ 'MESSAGES.LOADING' | translate }}</span>
    </div>
  </div>
}

@if (tabErrorKey) {
  <div class="mx-4 md:mx-6 my-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm" role="alert">
    {{ tabErrorKey | translate }}
  </div>
}

@if (componentsView$ | async; as rows) {
  <!-- Desktop table: matches the CRM table chrome from sales-order-lines-tab — first
       cell uses pl-6, last cell uses pr-6, middle cells use px-2, headers stay on a
       single line via whitespace-nowrap, and BASE_UOM is its own column on md+.
       Header alignment intentionally diverges from sales-order-lines-tab: every <th>
       is text-left here for visual consistency, while the numeric <td> values
       (QUANTITY, SORT_ORDER) keep text-right + tabular-nums for readability. The
       SORT_ORDER column is widened to w-32 so the translated "SORT ORDER" label fits
       within the cell padding; SKU is compensated down to w-28 to keep the table
       within a typical desktop viewport without horizontal scroll. -->
  <div class="hidden md:block w-full overflow-x-auto">
    <table class="w-full text-sm table-fixed">
      <thead>
        <tr class="bg-neutral-50 dark:bg-neutral-800/50 border-b border-neutral-200 dark:border-neutral-800">
          <th class="pl-6 pr-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase whitespace-nowrap">
            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.COMPONENT' | translate }}
          </th>
          <th class="px-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase whitespace-nowrap w-28 hidden lg:table-cell">
            {{ 'COMPONENTS.GOODS.FIELDS.SKU' | translate }}
          </th>
          <th class="px-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase whitespace-nowrap w-28">
            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.QUANTITY' | translate }}
          </th>
          <th class="px-2 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase whitespace-nowrap w-24 hidden md:table-cell">
            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.BASE_UOM' | translate }}
          </th>
          <th class="py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase whitespace-nowrap w-32 hidden md:table-cell"
              [class.pr-6]="isReadonly"
              [class.pr-2]="!isReadonly"
              [class.pl-2]="true">
            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.SORT_ORDER' | translate }}
          </th>
          @if (!isReadonly) {
            <th class="pl-2 pr-6 py-3 text-left text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase whitespace-nowrap w-24">
              {{ 'BUTTONS.ACTIONS' | translate }}
            </th>
          }
        </tr>
      </thead>

      <tbody [formGroup]="form">
        <ng-template #editRow>
          <tr class="bg-blue-50/40 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800">
            <td class="pl-6 pr-2 py-2 align-middle min-w-0 max-w-0 overflow-hidden">
              <app-search
                class="block w-full min-w-0"
                customClass="form-input--compact"
                formControlName="componentInventoryItemId"
                [items]="(childOptions$ | async) ?? []"
                [filterFn]="childFilterFn"
                [formatter]="childFormatter"
                [compareFn]="childCompareFn"
                [placeholderText]="'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.SELECT_PLACEHOLDER' | translate"
                [errorMessage]="'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.SELECT_REQUIRED' | translate"
                [resultTemplate]="childOptionTemplate"
              ></app-search>
              <ng-template #childOptionTemplate let-r="result" let-t="term">
                @if (r) {
                  <div class="flex flex-col">
                    <ngb-highlight class="font-semibold text-neutral-900 dark:text-neutral-50" highlightClass="hover-highlight" [result]="r.title" [term]="t"></ngb-highlight>
                    @if (r.subtitle) {
                      <ngb-highlight class="text-xs text-neutral-500 dark:text-neutral-400" highlightClass="hover-highlight" [result]="r.subtitle" [term]="t"></ngb-highlight>
                    }
                  </div>
                }
              </ng-template>
            </td>
            <td class="px-2 py-2 align-middle font-mono text-xs text-neutral-500 dark:text-neutral-400 hidden lg:table-cell truncate">
              {{ createChildSku }}
            </td>
            <td class="px-2 py-2 align-middle text-right min-w-0">
              <input
                type="number"
                min="0.0001"
                step="0.0001"
                class="form-input form-input--compact text-right tabular-nums w-full"
                formControlName="quantity"
                [class.is-invalid]="form.controls.quantity.touched && form.controls.quantity.invalid"
              />
            </td>
            <td class="px-2 py-2 align-middle text-xs text-neutral-500 dark:text-neutral-400 hidden md:table-cell whitespace-nowrap">
              {{ createChildBaseUom }}
            </td>
            <td class="py-2 align-middle hidden md:table-cell"
                [class.pr-6]="isReadonly"
                [class.pr-2]="!isReadonly"
                [class.pl-2]="true">
              <input
                type="number"
                min="0"
                step="1"
                class="form-input form-input--compact text-right tabular-nums"
                formControlName="sortOrder"
              />
            </td>
            @if (!isReadonly) {
              <td class="pl-2 pr-6 py-2 align-middle">
                <div class="flex gap-1.5 justify-end items-center">
                  <button
                    type="button"
                    class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    [disabled]="isSavingRow"
                    [title]="'BUTTONS.CANCEL' | translate"
                    [attr.aria-label]="'BUTTONS.CANCEL' | translate"
                    (click)="cancelEditing()"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    [disabled]="isSavingRow || form.invalid"
                    [title]="'BUTTONS.SAVE' | translate"
                    [attr.aria-label]="'BUTTONS.SAVE' | translate"
                    (click)="saveRow()"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                  </button>
                </div>
              </td>
            }
          </tr>

          @if (rowErrorKey) {
            <tr>
              <td [attr.colspan]="isReadonly ? 5 : 6" class="px-6 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm" role="alert">
                {{ rowErrorKey | translate }}
              </td>
            </tr>
          }
        </ng-template>

        @if (isCreatingRow) {
          <ng-container *ngTemplateOutlet="editRow"></ng-container>
        }

        @if (rows.length === 0 && !isCreatingRow) {
          <tr>
            <td [attr.colspan]="isReadonly ? 5 : 6" class="px-6 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
              {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.EMPTY' | translate }}
            </td>
          </tr>
        }

        @for (row of rows; track row.id) {
          @if (editingComponentId === row.id) {
            <ng-container *ngTemplateOutlet="editRow"></ng-container>
          } @else {
            <tr class="border-b border-neutral-100 dark:border-neutral-800 even:bg-neutral-50/40 dark:even:bg-neutral-900/20 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 hover:shadow-sm dark:hover:shadow-none transition-shadow">
              <td class="pl-6 pr-2 py-3">
                <div class="flex items-center gap-3 min-w-0">
                  <span
                    class="inline-flex items-center justify-center w-8 h-8 shrink-0 rounded-full text-xs font-semibold select-none"
                    [class]="row.avatarClass"
                    aria-hidden="true"
                  >
                    @if (row.initials) {
                      {{ row.initials }}
                    } @else {
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10m0-10L4 7m0 0v10l8 4" />
                      </svg>
                    }
                  </span>
                  <div class="min-w-0">
                    <div class="font-medium text-neutral-900 dark:text-neutral-50 break-words">{{ row.componentTitle }}</div>
                    @if (row.hasSku) {
                      <span class="inline-flex items-center px-2 py-0.5 mt-1 rounded-md bg-neutral-100 dark:bg-neutral-800 font-mono text-xs text-neutral-600 dark:text-neutral-300 lg:hidden">
                        {{ row.componentSku }}
                      </span>
                    }
                  </div>
                </div>
              </td>
              <td class="px-2 py-3 hidden lg:table-cell truncate">
                @if (row.hasSku) {
                  <span class="inline-flex items-center px-2 py-0.5 rounded-md bg-neutral-100 dark:bg-neutral-800 font-mono text-xs text-neutral-600 dark:text-neutral-300">
                    {{ row.componentSku }}
                  </span>
                }
              </td>
              <td class="px-2 py-3 text-right whitespace-nowrap">
                <span class="text-base font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums">{{ row.quantity | number: '1.0-4' }}</span>
              </td>
              <td class="px-2 py-3 text-xs text-neutral-500 dark:text-neutral-400 hidden md:table-cell whitespace-nowrap">
                {{ row.baseUomCode }}
              </td>
              <td class="py-3 text-right text-xs text-neutral-500 dark:text-neutral-400 hidden md:table-cell tabular-nums"
                  [class.pr-6]="isReadonly"
                  [class.pr-2]="!isReadonly"
                  [class.pl-2]="true">{{ row.sortOrder | number: '1.0-0' }}</td>
              @if (!isReadonly) {
                <td class="pl-2 pr-6 py-3">
                  <div class="flex gap-1.5 justify-end items-center">
                    <button
                      type="button"
                      class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      [disabled]="isCreatingRow || !!editingComponentId"
                      [title]="'BUTTONS.EDIT' | translate"
                      [attr.aria-label]="'BUTTONS.EDIT' | translate"
                      (click)="startEditRow(row.id)"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      class="inline-flex items-center justify-center w-7 h-7 shrink-0 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      [disabled]="isDeletingComponentId === row.id || isCreatingRow || !!editingComponentId"
                      [title]="'BUTTONS.REMOVE' | translate"
                      [attr.aria-label]="'BUTTONS.REMOVE' | translate"
                      (click)="deleteRow(row.id)"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
                      </svg>
                    </button>
                  </div>
                </td>
              }
            </tr>
          }
        }
      </tbody>
    </table>
  </div>

  <!-- Mobile cards: side padding (px-4) mirrors the desktop table inset so switching
       breakpoints does not feel jarring. UoM stays inline on the quantity line as
       there are no columns in this layout. -->
  <div class="md:hidden px-4" [formGroup]="form">
    <ng-template #editCardMobile>
      <div class="border-t border-blue-200 dark:border-blue-800 bg-blue-50/40 dark:bg-blue-900/10 py-3 flex flex-col gap-3">
        <app-search
          class="block w-full min-w-0"
          customClass="form-input--compact"
          formControlName="componentInventoryItemId"
          [items]="(childOptions$ | async) ?? []"
          [filterFn]="childFilterFn"
          [formatter]="childFormatter"
          [compareFn]="childCompareFn"
          [placeholderText]="'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.SELECT_PLACEHOLDER' | translate"
          [errorMessage]="'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.SELECT_REQUIRED' | translate"
        ></app-search>

        <div class="grid grid-cols-2 gap-x-3 gap-y-3">
          <label class="flex flex-col gap-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.QUANTITY' | translate }}
            <input
              type="number"
              min="0.0001"
              step="0.0001"
              class="form-input form-input--compact text-right tabular-nums"
              formControlName="quantity"
              [class.is-invalid]="form.controls.quantity.touched && form.controls.quantity.invalid"
            />
          </label>

          <label class="flex flex-col gap-1 text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
            {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.SORT_ORDER' | translate }}
            <input
              type="number"
              min="0"
              step="1"
              class="form-input form-input--compact text-right tabular-nums"
              formControlName="sortOrder"
            />
          </label>
        </div>

        @if (rowErrorKey) {
          <div class="px-3 py-2 rounded-md bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm" role="alert">
            {{ rowErrorKey | translate }}
          </div>
        }

        @if (!isReadonly) {
          <div class="flex gap-2 justify-end items-center">
            <button
              type="button"
              class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              [disabled]="isSavingRow"
              [title]="'BUTTONS.CANCEL' | translate"
              [attr.aria-label]="'BUTTONS.CANCEL' | translate"
              (click)="cancelEditing()"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <button
              type="button"
              class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400 text-white shadow-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
              [disabled]="isSavingRow || form.invalid"
              [title]="'BUTTONS.SAVE' | translate"
              [attr.aria-label]="'BUTTONS.SAVE' | translate"
              (click)="saveRow()"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </button>
          </div>
        }
      </div>
    </ng-template>

    @if (isCreatingRow) {
      <ng-container *ngTemplateOutlet="editCardMobile"></ng-container>
    }

    @if (rows.length === 0 && !isCreatingRow) {
      <div class="py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
        {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.EMPTY' | translate }}
      </div>
    }

    @for (row of rows; track row.id; let isOdd = $odd) {
      @if (editingComponentId === row.id) {
        <ng-container *ngTemplateOutlet="editCardMobile"></ng-container>
      } @else {
        <div
          class="border border-neutral-100 dark:border-neutral-800 rounded-lg p-3 mb-2 flex flex-col gap-2 hover:border-neutral-200 dark:hover:border-neutral-700 hover:shadow-sm dark:hover:shadow-none transition-shadow"
          [class.bg-neutral-50]="isOdd"
          [class.dark:bg-neutral-900/30]="isOdd"
        >
          <div class="flex items-start gap-3 min-w-0">
            <span
              class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-full text-sm font-semibold select-none"
              [class]="row.avatarClass"
              aria-hidden="true"
            >
              @if (row.initials) {
                {{ row.initials }}
              } @else {
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10m0-10L4 7m0 0v10l8 4" />
                </svg>
              }
            </span>
            <div class="flex flex-col gap-1 min-w-0">
              <div class="font-medium text-neutral-900 dark:text-neutral-50 break-words">{{ row.componentTitle }}</div>
              @if (row.hasSku) {
                <span class="inline-flex self-start items-center px-2 py-0.5 rounded-md bg-neutral-100 dark:bg-neutral-800 font-mono text-xs text-neutral-600 dark:text-neutral-300 break-all">
                  {{ row.componentSku }}
                </span>
              }
            </div>
          </div>
          <div class="flex flex-wrap gap-x-4 gap-y-1 text-sm">
            <span class="inline-flex items-baseline gap-1">
              <span class="text-[11px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400 mr-1">
                {{ 'COMPONENTS.INVENTORY_ITEM_OVERVIEW.COMPONENTS.QUANTITY' | translate }}:
              </span>
              <span class="text-base font-semibold text-neutral-900 dark:text-neutral-50 tabular-nums">{{ row.quantity | number: '1.0-4' }}</span>
              @if (row.baseUomCode) {
                <span class="text-[10px] uppercase tracking-wide text-neutral-500 dark:text-neutral-400 ml-1">{{ row.baseUomCode }}</span>
              }
            </span>
          </div>
          @if (!isReadonly) {
            <div class="flex gap-2 justify-end items-center">
              <button
                type="button"
                class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md border border-neutral-200 dark:border-neutral-700 text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                [disabled]="isCreatingRow || !!editingComponentId"
                [title]="'BUTTONS.EDIT' | translate"
                [attr.aria-label]="'BUTTONS.EDIT' | translate"
                (click)="startEditRow(row.id)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
              <button
                type="button"
                class="inline-flex items-center justify-center w-9 h-9 shrink-0 rounded-md border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                [disabled]="isDeletingComponentId === row.id || isCreatingRow || !!editingComponentId"
                [title]="'BUTTONS.REMOVE' | translate"
                [attr.aria-label]="'BUTTONS.REMOVE' | translate"
                (click)="deleteRow(row.id)"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M1 7h22M10 7V4a1 1 0 011-1h2a1 1 0 011 1v3" />
                </svg>
              </button>
            </div>
          }
        </div>
      }
    }
  </div>
}
